import React, { useState, useEffect } from 'react';
import {
  Container,
  Typography,
  Box,
  Paper,
  Grid,
  Card,
  CardContent,
  Button,
  TextField,
  Chip,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Alert,
  Stack,
  useTheme,
  alpha,
  Skeleton,
  FormControlLabel,
  Switch,
  Tooltip,
  Tab,
  Tabs
} from '@mui/material';
import {
  Schedule as ScheduleIcon,
  Edit as EditIcon,
  Add as AddIcon,
  Delete as DeleteIcon,
  AccessTime as TimeIcon,
  Event as EventIcon,
  Save as SaveIcon,
  Dashboard as TemplateIcon,
  PlayArrow as PlayArrowIcon
} from '@mui/icons-material';
import { useAuth } from '../context/AuthContext';
import api, { handleApiError } from '../utils/api';

const SessionQuotaManager = ({ facilityId, facilityName }) => {
  const { user } = useAuth();
  const theme = useTheme();
  
  const [sessionQuotas, setSessionQuotas] = useState({
    enabled: false,
    sessions: [],
    templates: []
  });
  const [memberTypes, setMemberTypes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [tabValue, setTabValue] = useState(0);
  
  // Session form state
  const [showSessionForm, setShowSessionForm] = useState(false);
  const [editingSession, setEditingSession] = useState(null);
  const [sessionForm, setSessionForm] = useState({
    startTime: '',
    endTime: '',
    daysOfWeek: [],
    memberTypeQuotas: [],
    description: ''
  });

  // Template form state
  const [showTemplateForm, setShowTemplateForm] = useState(false);
  const [templateForm, setTemplateForm] = useState({
    name: '',
    description: '',
    sessions: []
  });

  const daysOfWeek = [
    'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'
  ];

  // Load member types
  const loadMemberTypes = async () => {
    try {
      const response = await api.get('/member-types');
      if (response.data.success) {
        setMemberTypes(response.data.data);
      }
    } catch (error) {
      console.error('Error loading member types:', error);
    }
  };

  // Initialize member type quotas for new session
  const initializeMemberTypeQuotas = (memberTypes) => {
    return memberTypes.map(memberType => ({
      memberType: memberType._id,
      memberTypeName: memberType.name,
      memberTypeCode: memberType.code,
      maxSlots: memberType.defaultQuotas?.dailyLimit || 0,
      priority: memberType.priority || 5,
      requiresApproval: false
    }));
  };

  useEffect(() => {
    if (facilityId && user?.role === 'admin') {
      fetchSessionQuotas();
      loadMemberTypes();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [facilityId, user]);

  const fetchSessionQuotas = async () => {
    try {
      setLoading(true);
      setError('');
      
      const response = await api.get(`/session-quotas/facility/${facilityId}`);
      setSessionQuotas(response.data.data.sessionQuotas || {
        enabled: false,
        sessions: [],
        templates: []
      });
      
    } catch (error) {
      setError('Failed to load session quotas');
      console.error('Error fetching session quotas:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleToggleEnabled = async () => {
    try {
      setSaving(true);
      setError('');
      
      const updatedQuotas = {
        ...sessionQuotas,
        enabled: !sessionQuotas.enabled
      };
      
      await api.put(`/session-quotas/facility/${facilityId}`, {
        sessionQuotas: updatedQuotas
      });
      
      setSessionQuotas(updatedQuotas);
      setSuccess(updatedQuotas.enabled ? 'Session quotas enabled' : 'Session quotas disabled');
      
    } catch (error) {
      setError('Failed to update session quota settings');
    } finally {
      setSaving(false);
    }
  };

  const handleSessionFormChange = (field, value) => {
    if (field.includes('.')) {
      const [parent, child, subfield] = field.split('.');
      setSessionForm(prev => ({
        ...prev,
        [parent]: {
          ...prev[parent],
          [child]: {
            ...prev[parent][child],
            [subfield]: value
          }
        }
      }));
    } else {
      setSessionForm(prev => ({
        ...prev,
        [field]: value
      }));
    }
  };

  const handleDayToggle = (day) => {
    const currentDays = sessionForm.daysOfWeek;
    const updatedDays = currentDays.includes(day)
      ? currentDays.filter(d => d !== day)
      : [...currentDays, day];
    
    setSessionForm(prev => ({
      ...prev,
      daysOfWeek: updatedDays
    }));
  };

  const handleMemberTypeQuotaChange = (index, field, value) => {
    setSessionForm(prev => ({
      ...prev,
      memberTypeQuotas: prev.memberTypeQuotas.map((quota, i) => 
        i === index ? { ...quota, [field]: value } : quota
      )
    }));
  };

  const resetSessionForm = () => {
    setSessionForm({
      startTime: '',
      endTime: '',
      daysOfWeek: [],
      memberTypeQuotas: initializeMemberTypeQuotas(memberTypes),
      description: ''
    });
    setEditingSession(null);
  };

  const handleEditSession = (sessionIndex) => {
    const session = sessionQuotas.sessions[sessionIndex];
    setSessionForm({
      startTime: session.startTime,
      endTime: session.endTime,
      daysOfWeek: session.daysOfWeek,
      memberTypeQuotas: session.memberTypeQuotas || initializeMemberTypeQuotas(memberTypes),
      description: session.description || ''
    });
    setEditingSession(sessionIndex);
    setShowSessionForm(true);
  };

  const handleSaveSession = async () => {
    try {
      setSaving(true);
      setError('');

      // Validate form
      if (!sessionForm.startTime || !sessionForm.endTime || sessionForm.daysOfWeek.length === 0) {
        setError('Please fill in all required fields');
        return;
      }

      const updatedSessions = [...sessionQuotas.sessions];
      
      if (editingSession !== null) {
        updatedSessions[editingSession] = sessionForm;
      } else {
        updatedSessions.push(sessionForm);
      }

      const updatedQuotas = {
        ...sessionQuotas,
        sessions: updatedSessions
      };

      await api.put(`/session-quotas/facility/${facilityId}`, {
        sessionQuotas: updatedQuotas
      });

      setSessionQuotas(updatedQuotas);
      setShowSessionForm(false);
      resetSessionForm();
      setSuccess(editingSession !== null ? 'Session updated successfully' : 'Session added successfully');
      
    } catch (error) {
      setError('Failed to save session');
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteSession = async (sessionIndex) => {
    if (!window.confirm('Are you sure you want to delete this session?')) {
      return;
    }

    try {
      setSaving(true);
      setError('');

      const updatedSessions = sessionQuotas.sessions.filter((_, index) => index !== sessionIndex);
      const updatedQuotas = {
        ...sessionQuotas,
        sessions: updatedSessions
      };

      await api.put(`/session-quotas/facility/${facilityId}`, {
        sessionQuotas: updatedQuotas
      });

      setSessionQuotas(updatedQuotas);
      setSuccess('Session deleted successfully');
      
    } catch (error) {
      setError('Failed to delete session');
    } finally {
      setSaving(false);
    }
  };

  const getAvailabilityColor = (maxSlots) => {
    if (maxSlots === 0) return 'error';
    if (maxSlots <= 5) return 'warning';
    return 'success';
  };

  const formatTimeRange = (startTime, endTime) => {
    return `${startTime} - ${endTime}`;
  };

  const formatDays = (days) => {
    if (days.length === 7) return 'Every day';
    if (days.length === 5 && !days.includes('Saturday') && !days.includes('Sunday')) {
      return 'Weekdays';
    }
    if (days.length === 2 && days.includes('Saturday') && days.includes('Sunday')) {
      return 'Weekends';
    }
    return days.join(', ');
  };

  // Template functions
  const handleSaveTemplate = async () => {
    try {
      setSaving(true);
      setError('');

      // Validate form
      if (!templateForm.name.trim()) {
        setError('Template name is required');
        return;
      }

      const templateData = {
        name: templateForm.name,
        description: templateForm.description,
        sessions: sessionQuotas.sessions
      };

      await api.post(`/session-quotas/facility/${facilityId}/templates`, templateData);
      
      // Refresh session quotas to get updated templates
      await fetchSessionQuotas();
      setShowTemplateForm(false);
      setTemplateForm({ name: '', description: '', sessions: [] });
      setSuccess('Template created successfully');
      
    } catch (error) {
      setError(handleApiError(error));
    } finally {
      setSaving(false);
    }
  };

  const handleApplyTemplate = async (templateIndex) => {
    if (!window.confirm('Applying this template will replace all current session quotas. Continue?')) {
      return;
    }

    try {
      setSaving(true);
      setError('');

      await api.post(`/session-quotas/facility/${facilityId}/apply-template`, {
        templateIndex
      });
      
      // Refresh session quotas
      await fetchSessionQuotas();
      setSuccess('Template applied successfully');
      
    } catch (error) {
      setError(handleApiError(error));
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteTemplate = async (templateIndex) => {
    if (!window.confirm('Are you sure you want to delete this template?')) {
      return;
    }

    try {
      setSaving(true);
      setError('');

      await api.delete(`/session-quotas/facility/${facilityId}/templates/${templateIndex}`);
      
      // Refresh session quotas to get updated templates
      await fetchSessionQuotas();
      setSuccess('Template deleted successfully');
      
    } catch (error) {
      setError(handleApiError(error));
    } finally {
      setSaving(false);
    }
  };

  if (user?.role !== 'admin') {
    return (
      <Alert severity="warning">
        This feature is only accessible to administrators.
      </Alert>
    );
  }

  function TabPanel({ children, value, index, ...other }) {
    return (
      <div
        role="tabpanel"
        hidden={value !== index}
        id={`session-quota-tabpanel-${index}`}
        aria-labelledby={`session-quota-tab-${index}`}
        {...other}
      >
        {value === index && <Box sx={{ p: 3 }}>{children}</Box>}
      </div>
    );
  }

  return (
    <Container maxWidth="xl" sx={{ py: 3 }}>
      {/* Header */}
      <Box sx={{ 
        mb: 4,
        background: `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.1)} 0%, ${alpha(theme.palette.secondary.main, 0.05)} 100%)`,
        borderRadius: 3,
        p: 4,
        position: 'relative',
        overflow: 'hidden'
      }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <ScheduleIcon sx={{ fontSize: 48, mr: 2, color: 'primary.main' }} />
            <Box>
              <Typography variant="h3" sx={{ fontWeight: 700, mb: 0.5, color: 'primary.main' }}>
                Session Quota Management
              </Typography>
              <Typography variant="h6" color="text.secondary" sx={{ fontWeight: 400 }}>
                {facilityName} - Configure time-slot based quotas for different user types
              </Typography>
            </Box>
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <FormControlLabel
              control={
                <Switch
                  checked={sessionQuotas.enabled}
                  onChange={handleToggleEnabled}
                  disabled={saving}
                />
              }
              label="Enable Session Quotas"
            />
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={() => {
                resetSessionForm();
                setShowSessionForm(true);
              }}
              disabled={!sessionQuotas.enabled || saving}
              sx={{ borderRadius: 2 }}
            >
              Add Session
            </Button>
          </Box>
        </Box>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError('')}>
          {error}
        </Alert>
      )}

      {success && (
        <Alert severity="success" sx={{ mb: 3 }} onClose={() => setSuccess('')}>
          {success}
        </Alert>
      )}

      <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 3 }}>
        <Tabs value={tabValue} onChange={(e, newValue) => setTabValue(newValue)}>
          <Tab label="Sessions" icon={<TimeIcon />} />
          <Tab label="Templates" icon={<TemplateIcon />} />
          <Tab label="Usage Report" icon={<EventIcon />} />
        </Tabs>
      </Box>

      {/* Sessions Tab */}
      <TabPanel value={tabValue} index={0}>
        {loading ? (
          <Box>
            {[1, 2, 3].map(i => (
              <Card key={i} sx={{ mb: 2 }}>
                <CardContent>
                  <Skeleton width="60%" height={24} sx={{ mb: 1 }} />
                  <Skeleton width="40%" height={20} sx={{ mb: 2 }} />
                  <Box sx={{ display: 'flex', gap: 1 }}>
                    <Skeleton width={80} height={32} />
                    <Skeleton width={100} height={32} />
                    <Skeleton width={90} height={32} />
                  </Box>
                </CardContent>
              </Card>
            ))}
          </Box>
        ) : sessionQuotas.sessions.length === 0 ? (
          <Paper sx={{ p: 6, textAlign: 'center' }}>
            <ScheduleIcon sx={{ fontSize: 64, color: 'text.secondary', mb: 2 }} />
            <Typography variant="h6" color="text.secondary" gutterBottom>
              No Session Quotas Configured
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
              Add session-specific quotas to control facility access by time slots and user types.
            </Typography>
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={() => {
                resetSessionForm();
                setShowSessionForm(true);
              }}
              disabled={!sessionQuotas.enabled}
            >
              Add First Session
            </Button>
          </Paper>
        ) : (
          <Box>
            {sessionQuotas.sessions.map((session, index) => (
              <Card key={index} sx={{ mb: 2 }}>
                <CardContent>
                  <Box sx={{ display: 'flex', justifyContent: 'between', alignItems: 'flex-start', mb: 2 }}>
                    <Box sx={{ flex: 1 }}>
                      <Typography variant="h6" gutterBottom>
                        {formatTimeRange(session.startTime, session.endTime)}
                      </Typography>
                      <Typography variant="body2" color="text.secondary" gutterBottom>
                        {formatDays(session.daysOfWeek)}
                      </Typography>
                      {session.description && (
                        <Typography variant="body2" color="text.secondary">
                          {session.description}
                        </Typography>
                      )}
                    </Box>
                    <Stack direction="row" spacing={1}>
                      <IconButton size="small" onClick={() => handleEditSession(index)}>
                        <EditIcon />
                      </IconButton>
                      <IconButton 
                        size="small" 
                        color="error" 
                        onClick={() => handleDeleteSession(index)}
                      >
                        <DeleteIcon />
                      </IconButton>
                    </Stack>
                  </Box>

                  <Grid container spacing={2}>
                    {(session.memberTypeQuotas || []).map((quota, index) => {
                      const memberType = memberTypes.find(mt => mt._id === quota.memberType);
                      return (
                        <Grid size={{ xs: 12, md: 4 }} key={quota.memberType || index}>
                          <Paper sx={{ p: 2, textAlign: 'center' }}>
                            <Typography variant="subtitle2" gutterBottom>
                              {quota.memberTypeName || memberType?.name || 'Unknown'}
                            </Typography>
                            <Chip
                              label={`${quota.maxSlots} slots`}
                              color={getAvailabilityColor(quota.maxSlots)}
                              size="small"
                              sx={{ mb: 1 }}
                            />
                            <Typography variant="caption" display="block" color="text.secondary">
                              Priority: {quota.priority}
                            </Typography>
                            {quota.requiresApproval && (
                              <Typography variant="caption" display="block" color="warning.main">
                                Requires Approval
                              </Typography>
                            )}
                          </Paper>
                        </Grid>
                      );
                    })}
                  </Grid>
                </CardContent>
              </Card>
            ))}
          </Box>
        )}
      </TabPanel>

      {/* Templates Tab */}
      <TabPanel value={tabValue} index={1}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
          <Box>
            <Typography variant="h6" gutterBottom>Session Templates</Typography>
            <Typography variant="body2" color="text.secondary">
              Create and apply templates for quick session quota setup.
            </Typography>
          </Box>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => setShowTemplateForm(true)}
            disabled={!sessionQuotas.sessions.length}
          >
            Create Template
          </Button>
        </Box>

        {/* Templates List */}
        {sessionQuotas.templates && sessionQuotas.templates.length > 0 ? (
          <Grid container spacing={2}>
            {sessionQuotas.templates.map((template, index) => (
              <Grid size={{ xs: 12, md: 6 }} key={index}>
                <Card>
                  <CardContent>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
                      <Box>
                        <Typography variant="h6" gutterBottom>
                          {template.name}
                        </Typography>
                        {template.description && (
                          <Typography variant="body2" color="text.secondary" gutterBottom>
                            {template.description}
                          </Typography>
                        )}
                      </Box>
                      <Box>
                        <Tooltip title="Apply Template">
                          <IconButton 
                            size="small" 
                            color="primary"
                            onClick={() => handleApplyTemplate(index)}
                          >
                            <PlayArrowIcon />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Delete Template">
                          <IconButton 
                            size="small" 
                            color="error"
                            onClick={() => handleDeleteTemplate(index)}
                          >
                            <DeleteIcon />
                          </IconButton>
                        </Tooltip>
                      </Box>
                    </Box>
                    
                    <Typography variant="subtitle2" gutterBottom>
                      Sessions ({template.sessions.length})
                    </Typography>
                    <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                      {template.sessions.map((session, sessionIndex) => (
                        <Chip
                          key={sessionIndex}
                          label={`${session.startTime}-${session.endTime}`}
                          size="small"
                          variant="outlined"
                        />
                      ))}
                    </Box>
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>
        ) : (
          <Paper sx={{ p: 6, textAlign: 'center' }}>
            <TemplateIcon sx={{ fontSize: 64, color: 'text.secondary', mb: 2 }} />
            <Typography variant="h6" color="text.secondary" gutterBottom>
              No Templates Created
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
              Create templates from your existing session configurations for quick setup.
            </Typography>
            {sessionQuotas.sessions.length > 0 ? (
              <Button
                variant="contained"
                startIcon={<AddIcon />}
                onClick={() => setShowTemplateForm(true)}
              >
                Create First Template
              </Button>
            ) : (
              <Typography variant="body2" color="text.secondary">
                Add some session quotas first to create templates
              </Typography>
            )}
          </Paper>
        )}
      </TabPanel>

      {/* Usage Report Tab */}
      <TabPanel value={tabValue} index={2}>
        <Typography variant="h6" gutterBottom>Usage Analytics</Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
          View session quota utilization and booking patterns.
        </Typography>
        
        <Paper sx={{ p: 4, textAlign: 'center' }}>
          <EventIcon sx={{ fontSize: 48, color: 'text.secondary', mb: 2 }} />
          <Typography variant="body1" color="text.secondary">
            Usage reports coming soon
          </Typography>
        </Paper>
      </TabPanel>

      {/* Session Form Dialog */}
      <Dialog 
        open={showSessionForm} 
        onClose={() => setShowSessionForm(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          {editingSession !== null ? 'Edit Session Quota' : 'Add New Session Quota'}
        </DialogTitle>
        <DialogContent>
          <Grid container spacing={3} sx={{ mt: 1 }}>
            <Grid size={6}>
              <TextField
                fullWidth
                label="Start Time"
                type="time"
                value={sessionForm.startTime}
                onChange={(e) => handleSessionFormChange('startTime', e.target.value)}
                slotProps={{
                  inputLabel: { shrink: true }
                }}
                required
              />
            </Grid>

            <Grid size={6}>
              <TextField
                fullWidth
                label="End Time"
                type="time"
                value={sessionForm.endTime}
                onChange={(e) => handleSessionFormChange('endTime', e.target.value)}
                slotProps={{
                  inputLabel: { shrink: true }
                }}
                required
              />
            </Grid>

            <Grid size={12}>
              <Typography variant="subtitle2" gutterBottom>
                Days of Week *
              </Typography>
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                {daysOfWeek.map(day => (
                  <Chip
                    key={day}
                    label={day}
                    onClick={() => handleDayToggle(day)}
                    color={sessionForm.daysOfWeek.includes(day) ? 'primary' : 'default'}
                    variant={sessionForm.daysOfWeek.includes(day) ? 'filled' : 'outlined'}
                  />
                ))}
              </Box>
            </Grid>

            {(sessionForm.memberTypeQuotas || []).map((quota, index) => {
              const memberType = memberTypes.find(mt => mt._id === quota.memberType);
              return (
                <Grid size={12} key={quota.memberType || index}>
                  <Paper sx={{ p: 2 }}>
                    <Typography variant="subtitle2" gutterBottom>
                      {quota.memberTypeName || memberType?.name || 'Unknown'} Quota
                    </Typography>
                    <Grid container spacing={2}>
                      <Grid size={4}>
                        <TextField
                          fullWidth
                          label="Max Slots"
                          type="number"
                          value={quota.maxSlots}
                          onChange={(e) => handleMemberTypeQuotaChange(
                            index, 
                            'maxSlots', 
                            parseInt(e.target.value) || 0
                          )}
                          inputProps={{ min: 0, max: 100 }}
                          helperText="0 = Closed for this member type"
                        />
                      </Grid>
                      <Grid size={4}>
                        <TextField
                          fullWidth
                          label="Priority"
                          type="number"
                          value={quota.priority}
                          onChange={(e) => handleMemberTypeQuotaChange(
                            index, 
                            'priority',
                            parseInt(e.target.value) || 1
                          )}
                          inputProps={{ min: 1, max: 10 }}
                          helperText="1 = Highest priority, 10 = Lowest"
                        />
                      </Grid>
                      <Grid size={4}>
                        <FormControlLabel
                          control={
                            <Switch
                              checked={quota.requiresApproval}
                              onChange={(e) => handleMemberTypeQuotaChange(
                                index, 
                                'requiresApproval',
                                e.target.checked
                              )}
                            />
                          }
                          label="Requires Approval"
                        />
                      </Grid>
                    </Grid>
                  </Paper>
                </Grid>
              );
            })}

            <Grid size={12}>
              <TextField
                fullWidth
                multiline
                rows={2}
                label="Description"
                value={sessionForm.description}
                onChange={(e) => handleSessionFormChange('description', e.target.value)}
                placeholder="Optional description for this session..."
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowSessionForm(false)} disabled={saving}>
            Cancel
          </Button>
          <Button 
            variant="contained" 
            onClick={handleSaveSession}
            disabled={saving}
            startIcon={<SaveIcon />}
          >
            {saving ? 'Saving...' : (editingSession !== null ? 'Update' : 'Create')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Template Form Dialog */}
      <Dialog 
        open={showTemplateForm} 
        onClose={() => setShowTemplateForm(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Create Session Template</DialogTitle>
        <DialogContent>
          <Grid container spacing={3} sx={{ mt: 1 }}>
            <Grid size={12}>
              <TextField
                fullWidth
                label="Template Name"
                value={templateForm.name}
                onChange={(e) => setTemplateForm(prev => ({ ...prev, name: e.target.value }))}
                required
                helperText="A descriptive name for this template"
              />
            </Grid>
            <Grid size={12}>
              <TextField
                fullWidth
                multiline
                rows={3}
                label="Description"
                value={templateForm.description}
                onChange={(e) => setTemplateForm(prev => ({ ...prev, description: e.target.value }))}
                helperText="Optional description of when to use this template"
              />
            </Grid>
            <Grid size={12}>
              <Typography variant="subtitle2" gutterBottom>
                Sessions to Include ({sessionQuotas.sessions.length})
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                All current session quotas will be saved in this template.
              </Typography>
              <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                {sessionQuotas.sessions.map((session, index) => (
                  <Chip
                    key={index}
                    label={`${session.startTime}-${session.endTime} (${formatDays(session.daysOfWeek)})`}
                    size="small"
                    color="primary"
                    variant="outlined"
                  />
                ))}
              </Box>
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowTemplateForm(false)} disabled={saving}>
            Cancel
          </Button>
          <Button 
            variant="contained" 
            onClick={handleSaveTemplate}
            disabled={saving || !templateForm.name.trim()}
            startIcon={<SaveIcon />}
          >
            {saving ? 'Creating...' : 'Create Template'}
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default SessionQuotaManager;